// WebhookIntegrationsSection — Slack + Teams cards on /settings.
// Visible only to tenant_admin / platform_admin (RoleGuard wraps from parent).
import React, { useEffect, useState } from "react";
import { CheckCircle2, AlertCircle, Send } from "lucide-react";
import {
  useIntegrations,
  useUpdateIntegrations,
  useTestWebhook,
} from "../api/sitroom";
import { useStore } from "../store";

const SLACK = "#4A154B";
const TEAMS = "#5059C9";

const relativeTime = (iso) => {
  if (!iso) return null;
  try {
    const then = new Date(iso);
    const diffMs = Date.now() - then.getTime();
    const min = Math.floor(diffMs / 60000);
    if (min < 1) return "just now";
    if (min < 60) return `${min} min ago`;
    const hr = Math.floor(min / 60);
    if (hr < 24) return `${hr}h ago`;
    return then.toLocaleString();
  } catch {
    return null;
  }
};

const SlackLogo = () => (
  <svg viewBox="0 0 24 24" width="22" height="22" fill="currentColor" aria-hidden="true">
    <path d="M5 14.5a2.5 2.5 0 1 1 0-5h2.5v5H5zm1.25-7.25a2.5 2.5 0 1 1-2.5-2.5h2.5v2.5zM9.5 5a2.5 2.5 0 1 1 5 0v2.5h-5V5zM12 19a2.5 2.5 0 1 1 0-5h2.5v2.5A2.5 2.5 0 0 1 12 19zm-2.5-7.25v-2.5H7A2.5 2.5 0 1 1 9.5 7v2.5h2.5v2.25zm5-2.5h2.5a2.5 2.5 0 1 1-2.5 2.5v-2.5z" />
  </svg>
);

const TeamsLogo = () => (
  <svg viewBox="0 0 24 24" width="22" height="22" fill="currentColor" aria-hidden="true">
    <path d="M14 9V5.5A2.5 2.5 0 0 1 16.5 3a2.5 2.5 0 0 1 0 5H14zm-2 0H3v12h9V9zm6.5 0H21v9.5a3.5 3.5 0 0 1-7 0V14h2v4.5a1.5 1.5 0 0 0 3 0V11h-2V9h1.5z" />
  </svg>
);

const StatusLine = ({ lastSent, lastStatus, hasUrl }) => {
  if (!hasUrl) {
    return (
      <span
        style={{
          background: "rgba(255,255,255,0.05)",
          border: "1px solid rgba(255,255,255,0.10)",
          color: "#9198b0",
          fontFamily: "DM Mono, monospace",
          fontSize: 9,
          padding: "2px 8px",
          borderRadius: 3,
          letterSpacing: "1px",
        }}
        data-testid="webhook-status-not-configured"
      >
        NOT CONFIGURED
      </span>
    );
  }
  if (!lastSent) {
    return (
      <span
        style={{
          fontFamily: "DM Mono, monospace",
          fontSize: 10,
          color: "#9198b0",
          letterSpacing: "0.4px",
        }}
        data-testid="webhook-status-never"
      >
        Never sent
      </span>
    );
  }
  const ok = lastStatus === "ok";
  return (
    <span
      className="inline-flex items-center gap-1.5"
      style={{
        fontFamily: "DM Mono, monospace",
        fontSize: 10,
        color: ok ? "#4ed084" : "#ff7e7c",
        letterSpacing: "0.4px",
      }}
      data-testid={`webhook-status-${ok ? "ok" : "failed"}`}
    >
      {ok ? <CheckCircle2 size={11} /> : <AlertCircle size={11} />}
      {ok ? "Last sent" : "Last failed"}: {relativeTime(lastSent)}
    </span>
  );
};

const WebhookCard = ({
  type,
  title,
  accent,
  Logo,
  placeholder,
  current,
  data,
  onSave,
  onTest,
  saving,
  testing,
  testId,
}) => {
  const [url, setUrl] = useState(current || "");
  useEffect(() => {
    setUrl(current || "");
  }, [current]);

  const dirty = url !== (current || "");
  const isValid = url === "" || /^https?:\/\//i.test(url);

  return (
    <div
      className="rounded-md p-5"
      style={{
        background: "#111420",
        border: "1px solid rgba(255,255,255,0.12)",
      }}
      data-testid={testId}
    >
      <div className="flex items-center gap-3 mb-4">
        <span
          className="rounded-md flex items-center justify-center"
          style={{
            width: 38,
            height: 38,
            background: `${accent}22`,
            border: `1px solid ${accent}66`,
            color: accent,
          }}
        >
          <Logo />
        </span>
        <div className="flex-1">
          <div
            style={{
              fontFamily: "'Syne', system-ui, sans-serif",
              fontWeight: 800,
              fontSize: 16,
              color: "#f0f2f8",
            }}
          >
            {title}
          </div>
          <div
            style={{
              fontFamily: "DM Mono, monospace",
              fontSize: 10,
              color: "#9198b0",
              letterSpacing: "0.6px",
              marginTop: 2,
            }}
          >
            Incoming webhook URL
          </div>
        </div>
      </div>

      <input
        type="url"
        value={url}
        onChange={(e) => setUrl(e.target.value)}
        placeholder={placeholder}
        spellCheck={false}
        autoComplete="off"
        style={{
          width: "100%",
          background: "#0d0f18",
          border: "1px solid rgba(255,255,255,0.10)",
          borderRadius: 6,
          color: "#f0f2f8",
          fontFamily: "DM Mono, monospace",
          fontSize: 11,
          padding: "8px 10px",
          outline: "none",
        }}
        data-testid={`${testId}-url-input`}
      />
      {!isValid && (
        <div
          className="mt-2"
          style={{
            fontFamily: "DM Mono, monospace",
            fontSize: 10,
            color: "#ff7e7c",
            letterSpacing: "0.4px",
          }}
          data-testid={`${testId}-invalid`}
        >
          URL must start with http(s)://
        </div>
      )}

      <div className="flex items-center justify-between gap-2 mt-3 flex-wrap">
        <StatusLine
          lastSent={type === "slack" ? data?.slack_last_sent : data?.teams_last_sent}
          lastStatus={
            type === "slack" ? data?.slack_last_status : data?.teams_last_status
          }
          hasUrl={Boolean((current || "").trim())}
        />
        <div className="flex items-center gap-2 ml-auto">
          <button
            onClick={() => onTest(type)}
            disabled={testing || !current}
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md"
            style={{
              background: "transparent",
              border: "1px solid rgba(255,255,255,0.10)",
              color: current ? "#cfd1d8" : "#6b7388",
              fontFamily: "Syne, sans-serif",
              fontWeight: 600,
              fontSize: 11,
              letterSpacing: "0.4px",
              cursor: current ? "pointer" : "not-allowed",
              opacity: testing ? 0.6 : 1,
            }}
            data-testid={`${testId}-test-btn`}
          >
            <Send size={11} /> {testing ? "Sending…" : "Send test"}
          </button>
          <button
            onClick={() => onSave(type, url)}
            disabled={!dirty || !isValid || saving}
            className="px-3 py-2 rounded-md"
            style={{
              background: dirty && isValid ? accent : "rgba(255,255,255,0.06)",
              border: `1px solid ${dirty && isValid ? accent : "rgba(255,255,255,0.10)"}`,
              color: dirty && isValid ? "#fff" : "#6b7388",
              fontFamily: "Syne, sans-serif",
              fontWeight: 700,
              fontSize: 11,
              letterSpacing: "0.4px",
              cursor: dirty && isValid && !saving ? "pointer" : "not-allowed",
              opacity: saving ? 0.6 : 1,
            }}
            data-testid={`${testId}-save-btn`}
          >
            {saving ? "Saving…" : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
};

export const WebhookIntegrationsSection = () => {
  const { data, isLoading, error } = useIntegrations();
  const updateMut = useUpdateIntegrations();
  const testMut = useTestWebhook();
  const addToast = useStore((s) => s.addToast);

  const [savingType, setSavingType] = useState(null);
  const [testingType, setTestingType] = useState(null);

  if (error?.response?.status === 403) {
    // Hidden for non-admins (RoleGuard upstream already redirects most), keep guard local
    return null;
  }

  const handleSave = async (type, url) => {
    setSavingType(type);
    try {
      const body =
        type === "slack"
          ? { slack_webhook_url: url }
          : { teams_webhook_url: url };
      await updateMut.mutateAsync(body);
      addToast({
        type: "info",
        title: type === "slack" ? "Slack webhook saved" : "Teams webhook saved",
        body: url ? "Webhook URL updated." : "Webhook URL cleared.",
      });
    } catch (ex) {
      const detail = ex?.response?.data?.detail || ex.message || "Save failed";
      addToast({ type: "s1", title: "Save failed", body: String(detail) });
    } finally {
      setSavingType(null);
    }
  };

  const handleTest = async (type) => {
    setTestingType(type);
    try {
      await testMut.mutateAsync(type);
      addToast({
        type: "info",
        title: `Test message sent to ${type === "slack" ? "Slack" : "Teams"} ✓`,
        body: "Check the channel for the delivery.",
      });
    } catch (ex) {
      const detail =
        ex?.response?.data?.detail || ex.message || "Webhook delivery failed";
      addToast({
        type: "s1",
        title: "Webhook failed",
        body: typeof detail === "string" ? detail : "Check URL",
      });
    } finally {
      setTestingType(null);
    }
  };

  return (
    <div data-testid="webhook-integrations-section">
      <div
        className="mb-4 mt-8"
        style={{
          fontFamily: "DM Mono, monospace",
          fontSize: 10,
          letterSpacing: "1.5px",
          color: "#9198b0",
          textTransform: "uppercase",
        }}
      >
        Chat Webhooks
      </div>

      {isLoading ? (
        <div
          style={{
            fontFamily: "DM Mono, monospace",
            fontSize: 11,
            color: "#9198b0",
            padding: "20px 0",
          }}
          data-testid="webhook-integrations-loading"
        >
          Loading integrations…
        </div>
      ) : (
        <div className="grid lg:grid-cols-2 gap-4">
          <WebhookCard
            testId="webhook-card-slack"
            type="slack"
            title="Slack"
            accent={SLACK}
            Logo={SlackLogo}
            placeholder="https://hooks.slack.com/services/T000/B000/XXXX"
            current={data?.slack_webhook_url || ""}
            data={data}
            onSave={handleSave}
            onTest={handleTest}
            saving={savingType === "slack"}
            testing={testingType === "slack"}
          />
          <WebhookCard
            testId="webhook-card-teams"
            type="teams"
            title="Microsoft Teams"
            accent={TEAMS}
            Logo={TeamsLogo}
            placeholder="https://outlook.office.com/webhook/…"
            current={data?.teams_webhook_url || ""}
            data={data}
            onSave={handleSave}
            onTest={handleTest}
            saving={savingType === "teams"}
            testing={testingType === "teams"}
          />
        </div>
      )}
    </div>
  );
};
